import type { Dispatch, FC, SetStateAction } from 'react';
import { Fragment, useEffect, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { Filter, FilterGroup } from 'src/types/followUpInspirations';

import { styles } from './index.styles';

export const FILTER_PAGE_TEST_ID = 'filter-page';

interface FilterPageProps {
  onResult?: Dispatch<SetStateAction<object>>;
  filterGroups: FilterGroup[];
}

const FilterPage: FC<FilterPageProps> = ({ onResult, filterGroups }) => {
  const initialFormState = filterGroups.reduce(
    (acc, filterGroup) => ({
      ...acc,
      [filterGroup.category]: null,
    }),
    {},
  );
  const [formState, setFormState] = useState<{ [key: string]: Filter | null }>(
    initialFormState,
  );

  useEffect(() => {
    if (onResult) {
      if (Object.values(formState).every((value) => !!value)) {
        onResult(formState);
      } else {
        onResult({});
      }
    }
  }, [formState]);

  const getToggleButtonGroup = (filterGroup: FilterGroup) => {
    const handleChange = (
      _event: React.MouseEvent<HTMLElement>,
      newFilter: number,
    ) => {
      const selectedFilter = filterGroup.filters.find(
        (filter) => filter.id === newFilter,
      );

      if (selectedFilter) {
        setFormState((prevState) => ({
          ...prevState,
          [filterGroup.category]: selectedFilter,
        }));
      }
    };

    const gridColumns = Math.floor(filterGroup.filters.length / 3)
      ? 3
      : filterGroup.filters.length % 3;

    return (
      <ToggleButtonGroup
        exclusive
        onChange={handleChange}
        value={formState[filterGroup.category]?.id}
        sx={styles.toggleButtonGroup(gridColumns)}
      >
        {filterGroup.filters.map((filter, i) => (
          <ToggleButton
            value={filter.id}
            key={`${filterGroup.category}-${filter.id}`}
            sx={styles.gridItem((i % 3) + 1, Math.floor(i / 3) + 1)}
          >
            {filter.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  return (
    <FormControl fullWidth data-test-id={FILTER_PAGE_TEST_ID}>
      <Stack spacing={3.5} sx={styles.wrapper}>
        <Divider role="presentation" />

        {filterGroups.map((filterGroup, i, { length }) => (
          <Fragment key={filterGroup.category}>
            <Box>
              <Typography>{filterGroup.title}</Typography>
              {!!filterGroup.helpText && (
                <Typography variant="body3">{filterGroup.helpText}</Typography>
              )}
            </Box>
            {getToggleButtonGroup(filterGroup)}

            {i + 1 < length && <Divider role="presentation" />}
          </Fragment>
        ))}
      </Stack>
    </FormControl>
  );
};

export default FilterPage;
